/**
 * UpgradeModal
 *
 * In-context plan picker opened from a feature gate (Paper "Heatmaps · Upgrade
 * modal"): "{Feature} start on Business", Business and Scale side by side,
 * one orange CTA straight to Stripe. Plus is never shown. Accounts that
 * already have a subscription (or an open invoice) are handed to the full
 * pricing flow, which routes them to the Stripe portal.
 */

import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { HugeiconsIcon } from '@hugeicons/react'
import { Cancel01Icon, Loading01Icon } from '@hugeicons/core-free-icons'
import { Dialog, DialogPortal } from '@/components/ui/dialog'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useSubscription } from '@/lib/contexts/SubscriptionContext'
import { PRICING_PLANS, getPaymentLink, getPlanDisplayName } from '@/lib/constants/pricing'
import type { PricingPlan } from '@/lib/types/billing'
import { needsInvoicePortal } from '@/lib/utils/subscriptionEntitlement'
import { markTrialActivating } from '@/lib/utils/trialActivation'
import { track } from '@/lib/utils/track'
import { logPaywallShown, usePaywallScope } from '@/lib/observability/paywall'
import { cn } from '@/lib/utils'

const ENTERPRISE_CONTACT_URL = 'https://humblytics.com/request-demo'

/** Gate feature name → how the modal title reads it. */
const FEATURE_TITLE: Record<string, { label: string; plural: boolean }> = {
  Heatmaps: { label: 'Heatmaps', plural: true },
  Funnels: { label: 'Funnels', plural: true },
  'A/B Testing': { label: 'A/B tests', plural: true },
  'AI Copilot': { label: 'AI Copilot', plural: false },
}

const UPGRADE_FEATURE_KEY: Record<string, string> = {
  Heatmaps: 'heatmaps',
  Funnels: 'funnels',
  'A/B Testing': 'splitTesting',
  'AI Copilot': 'aiCopilot',
}

/** Business and Scale, in that order. Plus is retired from every CTA. */
export const UPGRADE_PLANS: PricingPlan[] = PRICING_PLANS.filter((p) => p.id === 'business' || p.id === 'scale')

export function buildUpgradeModalTitle(featureName: string, planName = 'Business'): string {
  const f = FEATURE_TITLE[featureName] ?? { label: featureName, plural: false }
  return `${f.label} ${f.plural ? 'start' : 'starts'} on ${planName}`
}

export interface UpgradeModalViewProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  intro: string
  plans: PricingPlan[]
  selectedId: string
  onSelect: (id: string) => void
  isAnnual: boolean
  onBillingChange: (annual: boolean) => void
  usedTrial: boolean
  onContinue: () => void
  isRedirecting?: boolean
}

export function UpgradeModalView({
  open,
  onOpenChange,
  title,
  intro,
  plans,
  selectedId,
  onSelect,
  isAnnual,
  onBillingChange,
  usedTrial,
  onContinue,
  isRedirecting = false,
}: UpgradeModalViewProps) {
  const selected = plans.find((p) => p.id === selectedId) ?? plans[0]
  const ctaLabel = usedTrial ? `Upgrade to ${selected.name}` : `Start 14-day ${selected.name} trial`
  const note = usedTrial
    ? `Next: Stripe checkout. Billed ${isAnnual ? 'yearly' : 'monthly'}, cancel anytime.`
    : 'Next: Stripe collects your card. $0 today, then billed on day 14 unless you cancel.'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[#171717]/45 dark:bg-black/60" />
        <DialogPrimitive.Content
          aria-describedby="upgrade-modal-intro"
          // Focus the dialog itself, not the close button, so no focus ring shows on open.
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            ;(e.currentTarget as HTMLElement).focus()
          }}
          className="fixed left-1/2 top-4 z-50 max-h-[calc(100svh-32px)] w-[calc(100vw-32px)] max-w-[640px] -translate-x-1/2 overflow-y-auto rounded-2xl bg-card px-5 pb-7 pt-[52px] shadow-[0_0_0_1px_rgb(9_9_11/0.08),0_24px_48px_-12px_rgb(0_0_0/0.18)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08),0_24px_48px_-12px_rgb(0_0_0/0.6)] focus:outline-none sm:top-14 sm:max-h-[calc(100svh-72px)] sm:px-10"
        >
          <DialogPrimitive.Close
            aria-label="Close"
            className="absolute right-4 top-4 flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted"
          >
            <HugeiconsIcon icon={Cancel01Icon} size={16} strokeWidth={2.2} />
          </DialogPrimitive.Close>

          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <DialogPrimitive.Title className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground">
                  {title}
                </DialogPrimitive.Title>
                <div role="radiogroup" aria-label="Billing period" className="flex rounded-lg bg-muted p-[3px]">
                  {[
                    { annual: false, label: 'Monthly' },
                    { annual: true, label: 'Annual · save up to 18%' },
                  ].map((o) => (
                    <button
                      key={o.label}
                      type="button"
                      role="radio"
                      aria-checked={isAnnual === o.annual}
                      onClick={() => onBillingChange(o.annual)}
                      className={cn(
                        'flex h-7 items-center rounded-[7px] px-3 text-[13px] font-medium leading-5 transition-colors',
                        isAnnual === o.annual
                          ? 'bg-card text-foreground shadow-[0_0_0_1px_rgb(9_9_11/0.05),0_1px_2px_rgb(0_0_0/0.04)]'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <DialogPrimitive.Description id="upgrade-modal-intro" className="text-sm leading-[22px] text-muted-foreground">
                {intro}
              </DialogPrimitive.Description>
            </div>

            <div role="radiogroup" aria-label="Plan" className="well flex flex-col gap-2">
              {plans.map((plan) => {
                const isSelected = plan.id === selected.id
                const price = isAnnual ? plan.annualPrice : plan.monthlyPrice
                return (
                  <button
                    key={plan.id}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => onSelect(plan.id)}
                    className={cn(
                      'flex flex-col gap-4 rounded-xl bg-card px-5 py-4 text-left transition-shadow',
                      isSelected
                        ? 'shadow-[0_0_0_1.5px_var(--color-foreground),0_10px_15px_-3px_rgb(0_0_0/0.05),0_4px_6px_-4px_rgb(0_0_0/0.05)]'
                        : 'shadow-[0_0_0_1px_rgb(9_9_11/0.05),0_10px_15px_-3px_rgb(0_0_0/0.04),0_4px_6px_-4px_rgb(0_0_0/0.04)] hover:shadow-[0_0_0_1px_var(--color-ring),0_10px_15px_-3px_rgb(0_0_0/0.04)]',
                    )}
                  >
                    <div className="flex gap-3">
                      <span
                        aria-hidden
                        className={cn(
                          'mt-0.5 size-4 shrink-0 rounded-full',
                          isSelected
                            ? 'shadow-[inset_0_0_0_5px_var(--color-foreground)]'
                            : 'shadow-[inset_0_0_0_1.5px_var(--color-input)]',
                        )}
                      />
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-semibold leading-5 text-foreground">{plan.name}</span>
                          {plan.badge && (
                            <span className="flex h-[22px] items-center whitespace-nowrap rounded-[7px] bg-primary-tint px-2 text-xs font-medium leading-4 text-primary-strong shadow-[inset_0_0_0_1px_var(--color-primary-tint-ring)]">
                              {plan.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-[13px] leading-5 text-muted-foreground">{plan.description}</p>
                        {!isSelected && (
                          <p className="pt-1 text-xs leading-[18px] text-muted-foreground">
                            {[plan.features[0], plan.features[2]].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-0.5">
                        <p className="flex items-baseline gap-1">
                          <span className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground tabular-nums">
                            {usedTrial ? `$${price}` : '$0'}
                          </span>
                          <span className="text-xs leading-[18px] text-muted-foreground">{usedTrial ? '/mo' : '14 days'}</span>
                        </p>
                        {(!usedTrial || isAnnual) && (
                          <p className="text-xs leading-[18px] text-muted-foreground tabular-nums">
                            {!usedTrial
                              ? isAnnual
                                ? `then $${plan.annualTotal.toLocaleString('en-US')}/yr`
                                : `then $${plan.monthlyPrice}/mo`
                              : `$${plan.annualTotal.toLocaleString('en-US')}/yr`}
                          </p>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <>
                        <span aria-hidden className="h-px shrink-0 bg-border" />
                        <ul className="grid grid-cols-1 gap-x-4 gap-y-2 pl-7 sm:grid-cols-2">
                          {plan.features.map((f) => (
                            <li key={f} className="flex items-center gap-2 text-xs leading-[18px] text-muted-foreground">
                              <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden className="shrink-0">
                                <path d="m5 12 5 5 9-10" fill="none" stroke="#737373" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              {f}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </button>
                )
              })}
            </div>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={onContinue}
                disabled={isRedirecting}
                className="flex h-11 items-center justify-center rounded-lg bg-primary text-sm font-medium leading-5 text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-70"
              >
                {isRedirecting ? <HugeiconsIcon icon={Loading01Icon} size={16} className="animate-spin" /> : ctaLabel}
              </button>
              <p className="text-center text-xs leading-[18px] text-muted-foreground">{note}</p>
            </div>

            <div className="flex items-center gap-3 rounded-xl bg-canvas py-3 pl-4 pr-3">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p className="text-sm font-medium leading-5 text-foreground">Enterprise</p>
                <p className="text-xs leading-[18px] text-muted-foreground">Unlimited events, team members, and priority support</p>
              </div>
              <a
                href={ENTERPRISE_CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-9 shrink-0 items-center rounded-lg bg-card px-3.5 text-[13px] font-medium leading-5 text-foreground shadow-[inset_0_0_0_1px_var(--color-input)] transition-colors hover:bg-muted"
              >
                Book a call
              </a>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}

interface UpgradeModalProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  featureName?: string
}

export function UpgradeModal({ open, onOpenChange, featureName }: UpgradeModalProps) {
  const {
    isUpgradeModalOpen,
    upgradeFeatureName,
    closeUpgradeModal,
    openPricingModal,
    usedTrial,
    currentPlan,
    planName,
    hasActiveSubscription,
    isReadOnly,
    subscriptionStatus,
    stripeSubscriptionId,
  } = useSubscription()
  const { token, user } = useAuth()
  const location = useLocation()
  const paywallScope = usePaywallScope()
  const [isAnnual, setIsAnnual] = useState(false)
  const [selectedId, setSelectedId] = useState('business')
  const [isRedirecting, setIsRedirecting] = useState(false)

  // Use external control if provided, otherwise use context
  const isOpen = open ?? isUpgradeModalOpen
  const feature = featureName ?? upgradeFeatureName ?? 'Premium Feature'
  const prevOpen = useRef(false)

  useEffect(() => {
    if (isOpen && !prevOpen.current) {
      logPaywallShown({
        surface: 'upgrade_modal',
        trigger: 'feature_denied',
        featureKey: UPGRADE_FEATURE_KEY[feature] ?? feature,
        pathname: location.pathname,
        scope: paywallScope,
      })
    }
    prevOpen.current = isOpen
  }, [isOpen, feature, location.pathname, paywallScope])

  const handleOpenChange = onOpenChange ?? ((next: boolean) => {
    if (!next) closeUpgradeModal()
  })

  const handleContinue = () => {
    // A live subscription changes plan in the Stripe portal, and an open
    // invoice is paid there: the pricing flow routes both. Only a fresh
    // checkout goes straight to Stripe from here.
    if (hasActiveSubscription || isReadOnly || needsInvoicePortal(subscriptionStatus, stripeSubscriptionId)) {
      handleOpenChange(false)
      openPricingModal()
      return
    }
    const plan = UPGRADE_PLANS.find((p) => p.id === selectedId) ?? UPGRADE_PLANS[0]
    setIsRedirecting(true)
    track('stripe_checkout_initiated', token, {
      plan: plan.id,
      billing: isAnnual ? 'annual' : 'monthly',
      is_trial: !usedTrial,
      trigger: 'upgrade_modal',
      feature,
    })
    if (!usedTrial) markTrialActivating()
    window.location.href = getPaymentLink(plan, isAnnual, usedTrial, user?.email, user?.userId)
  }

  const current = getPlanDisplayName(currentPlan, planName)
  const intro =
    currentPlan === 'free'
      ? 'Your Free plan covers analytics. Both plans add heatmaps, funnels, A/B tests and revenue attribution. They differ by volume, not tools.'
      : `Your ${current} plan doesn't include this. Both plans add heatmaps, funnels, A/B tests and revenue attribution. They differ by volume, not tools.`

  return (
    <UpgradeModalView
      open={isOpen}
      onOpenChange={handleOpenChange}
      title={buildUpgradeModalTitle(feature)}
      intro={intro}
      plans={UPGRADE_PLANS}
      selectedId={selectedId}
      onSelect={setSelectedId}
      isAnnual={isAnnual}
      onBillingChange={setIsAnnual}
      usedTrial={usedTrial}
      onContinue={handleContinue}
      isRedirecting={isRedirecting}
    />
  )
}

export default UpgradeModal
